import { Link } from 'react-router-dom';
import { ForkKnife, Sparkle, ArrowRight, CalendarPlus } from '@phosphor-icons/react';
import type { MealPlan } from '@researcher/shared';

const MEAL_ORDER: Record<string, number> = { breakfast: 0, lunch: 1, dinner: 2, snack: 3 };

export default function MealPlanCard({ plan }: { plan: MealPlan }) {
  const previewDays = plan.days.slice(0, 2);
  const totalMeals = plan.days.reduce((n, d) => n + d.meals.length, 0);
  const notes = plan.personalization?.notes ?? [];

  return (
    <div className="mt-3 rounded-xl border border-pink-200 dark:border-pink-800 bg-orange-50 dark:bg-gray-800 overflow-hidden not-prose">
      <div className="px-3 py-2.5">
        <div className="flex items-center gap-2 mb-1.5">
          <ForkKnife size={18} weight="duotone" className="text-pink-500" />
          <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">{plan.title}</span>
          {(plan.weekNumber ?? 1) > 1 && (
            <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-pink-600 dark:text-pink-400 bg-pink-100 dark:bg-pink-900/30 rounded-full px-1.5 py-0.5">Week {plan.weekNumber}</span>
          )}
        </div>
        <p className="text-xs text-gray-600 dark:text-gray-300 mb-2">{plan.summary}</p>

        {notes.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2.5">
            {notes.slice(0, 4).map((n, i) => (
              <span key={i} className="inline-flex items-center gap-1 text-[11px] bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300 rounded-full px-2 py-0.5">
                <Sparkle size={10} weight="fill" /> {n}
              </span>
            ))}
          </div>
        )}

        <div className="space-y-2 mb-2.5">
          {previewDays.map((d, i) => (
            <div key={i}>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-0.5">{d.label}</p>
              <ul className="space-y-0.5">
                {[...d.meals].sort((a, b) => (MEAL_ORDER[a.mealType] ?? 9) - (MEAL_ORDER[b.mealType] ?? 9)).map((m) => (
                  <li key={m.id} className="text-xs text-gray-700 dark:text-gray-200">
                    <span className="capitalize text-gray-400 dark:text-gray-500">{m.mealType}:</span> {m.name}
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {plan.days.length > 2 && (
            <p className="text-[11px] text-gray-400 dark:text-gray-500">+ {plan.days.length - 2} more day{plan.days.length - 2 > 1 ? 's' : ''} · {totalMeals} meals total</p>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
          <Link
            to={`/health/meal-plans/${plan.id}`}
            className="inline-flex items-center gap-1 text-xs font-medium text-pink-600 dark:text-pink-400 hover:underline"
          >
            View full plan{plan.shoppingList && plan.shoppingList.length > 0 ? ' + shopping list' : ''}
            <ArrowRight size={12} weight="bold" />
          </Link>
          <Link
            to={`/chat?prompt=${encodeURIComponent(`Plan the next week of my "${plan.title}" meal plan`)}`}
            className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-pink-600 dark:hover:text-pink-400"
          >
            <CalendarPlus size={13} /> Plan next week
          </Link>
        </div>
      </div>
    </div>
  );
}
